import { useCallback, useEffect, useState } from "react";

import { Paths } from "configs";
import { usePushRoute } from "hooks";
import { useAppDispatch, useAppSelector } from "stores";
import { authSlice } from "stores/Auth";
import { saveLocalStorage } from "utils";

import { SidebarProps } from "./Sidebar.types";

export const useSidebar = ({
  defaultLanguage,
  languages,
  onLanguageChange,
  onBlur,
}: Pick<
  SidebarProps,
  "defaultLanguage" | "languages" | "onLanguageChange" | "onBlur"
>) => {
  const dispatch = useAppDispatch();
  const { pushRoute } = usePushRoute();

  const [languageAnchorEl, setLanguageAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [lang, setLang] = useState(defaultLanguage ?? languages?.[0]?.id);

  const organizations = useAppSelector((state) => state.auth.organizations);
  const currentOrg = useAppSelector((state) => state.auth.currentOrganization);
  const [orgAnchorEl, setOrgAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(
    function languageChange() {
      if (!lang) return;
      onLanguageChange?.(lang);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang]
  );

  useEffect(function init() {
    const containerElm = document.querySelector(
      ".menu-container"
    ) as HTMLElement;
    const wrapperElm = document.querySelector(".menu-wrapper") as HTMLElement;
    const barTrackElm = document.querySelector(
      ".menu-bar-track"
    ) as HTMLElement;
    const scrollbarElm = document.querySelector(
      ".menu-scrollbar"
    ) as HTMLElement;
    const placeholderElm = document.querySelector(
      ".menu-bar-placeholder"
    ) as HTMLElement;
    const menuListElm = document.querySelector(".menu-list");

    if (
      !containerElm ||
      !wrapperElm ||
      !barTrackElm ||
      !scrollbarElm ||
      !placeholderElm ||
      !menuListElm
    )
      return;

    const placeholderVar = "--menu-bar-placeholder-display";
    const scrollbarHeightVar = "--menu-scrollbar-height";
    const scrollbarTopVar = "--menu-scrollbar-top";
    const dataStartFromPageY = "data-start-from-page-y";
    const sidebarHeaderHeight = 67; // IMPORTANT: 67 is the Scrollbar's pageY starting position relative to the Placeholder
    const defaultPageY = "0";

    scrollbarElm.setAttribute(dataStartFromPageY, defaultPageY);

    const onWrapperScroll = () => {
      const { scrollTop, scrollHeight, offsetHeight } = wrapperElm;
      const percent = (scrollTop * 100) / scrollHeight;
      barTrackElm.style.setProperty(
        scrollbarTopVar,
        `${(percent * offsetHeight) / 100}px`
      );
    };

    const onContainerMouseEnter = () => {
      const { scrollHeight, offsetHeight } = wrapperElm;
      if (scrollHeight === offsetHeight) {
        barTrackElm.style.setProperty(scrollbarHeightVar, "0px");
        barTrackElm.style.setProperty(scrollbarTopVar, "0px");
        return;
      }
      const scrollbarHeight = offsetHeight * (offsetHeight / scrollHeight);
      barTrackElm.style.setProperty(scrollbarHeightVar, `${scrollbarHeight}px`);
      onWrapperScroll();
    };

    const onBarTrackMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      const { scrollHeight, offsetHeight } = wrapperElm;
      const scrollToY = e.pageY - sidebarHeaderHeight;
      const percent = (scrollToY * 100) / offsetHeight;
      wrapperElm.scrollTo({
        top: (percent * scrollHeight) / 100,
        behavior: "smooth",
      });
    };

    const onPlaceholderMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const { scrollHeight, offsetHeight } = wrapperElm;
      const scrollbarHeight = offsetHeight * (offsetHeight / scrollHeight);
      const remainHeight = offsetHeight - scrollbarHeight;
      const startFromY = Number(
        scrollbarElm.getAttribute(dataStartFromPageY) ?? defaultPageY
      );
      const scrollToY = e.pageY - startFromY - sidebarHeaderHeight;
      let nextScrollTo;
      if (scrollToY < 0) {
        nextScrollTo = 0;
      } else if (scrollToY >= remainHeight) {
        nextScrollTo = remainHeight;
      } else {
        nextScrollTo = scrollToY;
      }
      const percent = (nextScrollTo * 100) / offsetHeight;
      wrapperElm.scrollTo({ top: (percent * scrollHeight) / 100 });
    };

    const onPlaceholderMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      const target = e.target as HTMLElement;
      barTrackElm.style.setProperty(placeholderVar, "none");
      target.removeEventListener("mouseup", onPlaceholderMouseUp as never);
      target.removeEventListener("mousemove", onPlaceholderMouseMove as never);
    };

    const onScrollbarMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const { scrollTop, scrollHeight, offsetHeight } = wrapperElm;
      const percent = (scrollTop * 100) / scrollHeight;
      scrollbarElm.setAttribute(
        dataStartFromPageY,
        (
          e.pageY -
          (percent * offsetHeight) / 100 -
          sidebarHeaderHeight
        ).toString() // IMPORTANT: 67 is the Scrollbar's pageY starting position relative to the Placeholder
      );
      barTrackElm.style.setProperty(placeholderVar, "block");
      placeholderElm.addEventListener("mouseup", onPlaceholderMouseUp);
      placeholderElm.addEventListener("mousemove", onPlaceholderMouseMove);
    };

    const onScrollbarMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      barTrackElm.style.setProperty(placeholderVar, "none");
      placeholderElm.removeEventListener("mouseup", onPlaceholderMouseUp);
      placeholderElm.removeEventListener("mousemove", onPlaceholderMouseMove);
    };

    const resizeObserverInstance = new ResizeObserver(onContainerMouseEnter);

    containerElm.addEventListener("mouseenter", onContainerMouseEnter);
    wrapperElm.addEventListener("scroll", onWrapperScroll);
    barTrackElm.addEventListener("mousedown", onBarTrackMouseDown);
    scrollbarElm.addEventListener("mousedown", onScrollbarMouseDown);
    scrollbarElm.addEventListener("mouseup", onScrollbarMouseUp);
    resizeObserverInstance.observe(wrapperElm);
    resizeObserverInstance.observe(menuListElm);

    // eslint-disable-next-line consistent-return
    return () => {
      containerElm.removeEventListener("mouseenter", onContainerMouseEnter);
      wrapperElm.removeEventListener("scroll", onWrapperScroll);
      barTrackElm.removeEventListener("mousedown", onBarTrackMouseDown);
      scrollbarElm.removeEventListener("mousedown", onScrollbarMouseDown);
      scrollbarElm.removeEventListener("mouseup", onScrollbarMouseUp);
      resizeObserverInstance.unobserve(wrapperElm);
      resizeObserverInstance.unobserve(menuListElm);
    };
  }, []);

  const handleLanguagePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguagePopoverClose = () => {
    setLanguageAnchorEl(null);
    onBlur?.();
  };

  const handleLanguageChange = (value: string) => {
    handleLanguagePopoverClose();
    setLang(value);
  };

  const handleOrgPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOrgAnchorEl(event.currentTarget);
  };

  const handleOrgPopoverClose = () => {
    setOrgAnchorEl(null);
    onBlur?.();
  };

  const handleOrgChange = (value: number) => {
    const orgSelected = organizations?.find((org) => org.id === value);
    dispatch(authSlice.actions.changeOrg(orgSelected));
    saveLocalStorage("org", value);
    window.location.href = "/";
    handleOrgPopoverClose();
  };

  const goToProfile = useCallback(() => {
    pushRoute({}, `${Paths.EmployeeDetail}/${currentOrg?.employeeId || ""}`, {
      isReset: true,
    });
  }, [currentOrg?.employeeId, pushRoute]);

  return {
    language: {
      ref: languageAnchorEl,
      value: lang,
      open: handleLanguagePopoverOpen,
      close: handleLanguagePopoverClose,
      change: handleLanguageChange,
    },
    organization: {
      ref: orgAnchorEl,
      value: currentOrg,
      options: organizations,
      open: handleOrgPopoverOpen,
      close: handleOrgPopoverClose,
      change: handleOrgChange,
    },
    goToProfile,
  };
};
